import React from 'react';
import Section from './Section';
import ShieldCheckIcon from './icons/ShieldCheckIcon';
import CurrencyDollarIcon from './icons/CurrencyDollarIcon';
import CheckIcon from './icons/CheckIcon';
import { CORE_BENEFITS_LIST } from '../constants';

const BenefitsSection: React.FC = () => {
  return (
    <Section id="benefits" title="Benefits" className="bg-white">
      <div className="space-y-16">
        {/* What We Do */}
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 text-lg text-gray-600">
            <h3 className="text-2xl font-semibold text-emerald-700 mb-4">Our Mission</h3>
            <img 
              src="/purchased-on-premises-labels-with-cans.png" 
              alt="Photo of Purchased On Premises Labels" 
              className="mx-auto mb-6 rounded-lg max-w-48 h-auto float-right" 
            />
            <p>
              Our 'Purchased on Premises' labels are meticulously designed to help your golf course sell more alcoholic beverages for on-course consumption. More importantly, they demonstrate your unwavering commitment to full compliance with liquor licensing requirements.
            </p>
            <p>
              We achieve this by providing an affordable and highly visible way to easily identify beverages purchased on your course. This simple system significantly discourages patrons from bringing their own alcoholic drinks, making enforcement straightforward for your marshals and other on-course staff.
            </p>
          </div>
          <div className="space-y-8">
            <div className="flex items-start p-6 bg-emerald-50 rounded-xl shadow-md">
              <ShieldCheckIcon className="w-12 h-12 text-emerald-500 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-xl font-semibold text-emerald-700 mb-2">Ensure Compliance</h4>
                <p className="text-gray-600">Visibly demonstrate your adherence to liquor laws and promote responsible consumption on your premises.</p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-sky-50 rounded-xl shadow-md">
              <CurrencyDollarIcon className="w-12 h-12 text-sky-500 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-xl font-semibold text-sky-700 mb-2">Increase Revenue</h4>
                <p className="text-gray-600">By discouraging outside beverages, you naturally boost sales of your own stock, directly impacting your bottom line.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Impact */}
        <div className="grid md:grid-cols-2 gap-12 items-center pt-12 border-t border-gray-200">
           <div className="flex justify-center items-center md:order-last">
            <img 
              src="/beer-stack.jpg" 
              alt="Graph illustrating size of 300 cans of beer" 
              className="rounded-lg max-w-full max-h-80 h-auto"
            />
          </div>
          <div className="space-y-6">
            <div className="flex items-center text-emerald-600 mb-4">
              <CurrencyDollarIcon className="w-10 h-10 mr-3" />
              <h3 className="text-2xl font-semibold">Unlock Hidden Revenue</h3>
            </div>
            <p className="text-lg text-gray-600">
              Consider this: if you price your beer at $5 a can and sell 20,000 cans annually. Our program typically increases sales by 15-20%. A conservative 15% rise means an extra 3,000 cans sold.
            </p>
            <p className="text-2xl font-bold text-emerald-700">
              That's an additional $15,000 in revenue!
            </p>
            <p className="text-lg text-gray-600">
              This increase alone could cover the cost of a new beer cart or other course improvements.
            </p>
          </div>
        </div>

        {/* Key Benefits */}
        <div className="pt-12 border-t border-gray-200">
          <h3 className="text-2xl font-semibold text-emerald-700 mb-8 text-center">Key Benefits of Our Program</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {CORE_BENEFITS_LIST.map((benefit, index) => (
              <div key={index} className="flex items-start p-6 bg-gray-50 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                <CheckIcon className="w-8 h-8 text-emerald-500 mr-4 mt-1 flex-shrink-0" />
                <p className="text-gray-700 text-lg">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};

export default BenefitsSection;